import React, { useContext } from 'react'
import moment from 'moment'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import FlashHotelOptionPriceDetails from './FlashHotelOptionPriceDetails'
import GeoContext from 'contexts/geoContext'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferPackagePartnershipLabel from 'components/Common/Labels/OfferPackagePartnershipLabel'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { getOnlyNRoomLeftLabel } from 'lib/offer/getUrgencyLabels'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import InclusionsV2Section from 'components/OfferPage/FlashHotel/InclusionsV2Section'
import InclusionsSection from 'components/OfferPage/FlashHotel/InclusionsSection'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import config from 'constants/config'
import AccommodationPackageTile, { AccommodationPackageTileContent, AccommodationPackageTilePricing } from '../Accommodation/AccommodationPackageTile'
import AccommodationPackageTileDetails from '../Accommodation/AccommodationPackageTileDetails'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

interface Props {
  optionNumber: number;
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  checkIn: moment.Moment;
  duration: number;
  flightDestination?: App.Airport;
  previewMode: boolean;
  showFlightsPrice: boolean;
  airports: App.Airport[];
}

function FlashHotelRateOption(props: Props) {
  const {
    offer,
    packageView,
    checkIn,
    duration,
    optionNumber,
    flightDestination,
    previewMode,
    showFlightsPrice,
    airports,
  } = props

  const { partnership } = useContext(GeoContext)
  const availableForPurchase = useIsAvailableForPurchase()
  const {
    package: pkg,
  } = packageView

  const showPartnership =
    (packageView.hotelAvailable &&
      !!partnership?.hasEarn &&
      offer.canEarnPartnershipPoints &&
      pkg.partnership &&
      partnership?.code !== VIRGIN_VELOCITY_PARTNERSHIP_CODE)

  const showPrices = (packageView.capacityValid && packageView.hotelAvailable) || previewMode

  const inclusions = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusions = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))

  const hasInclusionsV1 = pkg.inclusions && pkg.inclusions.length > 0

  const urgencyLabel = getOnlyNRoomLeftLabel((packageView.availableRooms ?? 0), true, config.RATE_OPTION_ONLY_N_ROOMS_LEFT_THRESHOLD)

  const isHotelAndFlightsToggleEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageHotelAndFlightsToggle)
  const showFlightInclusion = isHotelAndFlightsToggleEnabled && showFlightsPrice

  const destinationAirport = airports.find(({ code }) => code === offer.flightDestinationPort)

  const flightInclusion: App.Inclusion | undefined = (showFlightInclusion && destinationAirport) ? {
    text: `Return flights to ${destinationAirport.name} for all guests`,
    icon: 'plane_departure',
    category: 'mocked',
    isHighlighted: true,
  } : undefined

  return (
    <AccommodationPackageTile data-testid={pkg.uniqueKey}>
      <AccommodationPackageTileContent>
        <AccommodationPackageTileDetails
          packageNumber={optionNumber}
          // @ts-expect-error not App.HotelPackage
          roomPackage={packageView.package}
        >
          {availableForPurchase && offer.property && pkg.roomRate && <FlashCancellationPolicies
            checkInDate={checkIn}
            propertyTimezoneOffset={offer.property.timezoneOffset}
            cancellationPolicy={pkg.roomRate.cancellationPolicy}
          />}
          {!!showPartnership && <OfferPackagePartnershipLabel
            offer={offer}
            price={packageView.hotelPrice}
          />}
          {urgencyLabel && <LabelGroup>
            <UrgencyLabel variant="critical1">{urgencyLabel}</UrgencyLabel>
          </LabelGroup>}
        </AccommodationPackageTileDetails>
        <div>
          <LargeContentToggle
            maxHeight={208}
            showMoreText="View more inclusions"
            showLessText="View less inclusions"
          >
            {hasInclusionsV1 && (bonusInclusions || inclusions) && <InclusionsSection inclusions={inclusions} bonusInclusions={bonusInclusions} />}
            {!hasInclusionsV1 && packageView.package.inclusionsV2 && <InclusionsV2Section inclusions={packageView.package.inclusionsV2} flightInclusion={flightInclusion} />}
          </LargeContentToggle>
        </div>
      </AccommodationPackageTileContent>
      <AccommodationPackageTilePricing>
        {showPrices && <FlashHotelOptionPriceDetails
          packageView={packageView}
          flightDestination={flightDestination}
        />}
      </AccommodationPackageTilePricing>
    </AccommodationPackageTile>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  partnership: appState.geo.partnership,
  previewMode: appState.config.previewMode,
  showFlightsPrice: state.flights,
  airports: appState.geo.airports,
}))(FlashHotelRateOption)
